import { AdmDataGrid } from "@components/common/data-grid/adm-data-grid";
import { useMemberProjectsTableColumns } from "@pages/member-profile/tab-contents/member-project-tab/use-member-projects-table-columns";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchMemberProjects } from "@store/member/member-thunks";
import { BaseMemberIdProps } from "@custom-types/base-types";
import { memberStateSelector } from "@store/member/member-slice";
import { Spinner } from "@components/common/spinner/spinner";
import { ContentLayout } from "@pages/content-layout";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { useAppNavigation } from "@router/use-app-navigation";

const ERROR_MESSAGE = "Could not load projects of the member";

/** Component showing the list of projects in which a member belongs to */
export function MemberProjects({
  memberId,
}: BaseMemberIdProps): React.JSX.Element {
  const { handleErrorWithToast } = useErrorHandler();
  const [shouldShowErrorMessage, setShouldShowErrorMessage] =
    useState<boolean>(false);

  const columns = useMemberProjectsTableColumns();

  const { membersProjects, fetchingMember } =
    useAppSelector(memberStateSelector);

  const { navigateToProfilePage } = useAppNavigation();

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchMemberProjects({ memberId }))
      .unwrap()
      .catch((error) => {
        handleErrorWithToast({
          id: `MemberProjects-${Date.now().toString()}`,
          title: ERROR_MESSAGE,
          error,
        });
        setShouldShowErrorMessage(true);
      });
  }, [dispatch, handleErrorWithToast, memberId]);

  if (fetchingMember.membersProjects === "pending") {
    return <Spinner />;
  }

  return (
    <ContentLayout contentSx={{ overflowY: "hidden" }}>
      <AdmDataGrid
        entities={membersProjects}
        uniqueIdKey="projectId"
        tableType="member-projects-table"
        columns={columns}
        errorMessage={ERROR_MESSAGE}
        shouldShowErrorMessage={shouldShowErrorMessage}
        onRowClick={(projectId) =>
          navigateToProfilePage({
            subjectType: "PROJECT",
            subjectId: projectId,
          })
        }
      />
    </ContentLayout>
  );
}
